import L from "leaflet";

// Altitude bands, warm to cool — the convention every ADS-B display uses, so it reads correctly
// to anyone who has seen one before. The point is to make "low and close" pop out of a screen
// full of cruising traffic at a glance, which is the whole question this app answers.
export const ALTITUDE_BANDS: Array<{ max: number; color: string }> = [
  { max: 2000, color: "#ff5c33" },
  { max: 6000, color: "#ff9c33" },
  { max: 12000, color: "#ffd633" },
  { max: 20000, color: "#7ee07e" },
  { max: 30000, color: "#4dc3ff" },
  { max: 40000, color: "#6f8cff" },
  { max: Infinity, color: "#b47dff" },
];

const GROUND_COLOR = "#8ea0c0";

export function altitudeColor(alt: number | undefined, onGround: boolean | undefined): string {
  if (onGround) return GROUND_COLOR;
  if (alt === undefined) return GROUND_COLOR;
  return ALTITUDE_BANDS.find((b) => alt < b.max)!.color;
}

// A plan-view aircraft silhouette pointing north, drawn once and rotated per marker.
const SILHOUETTE =
  "M16 2 L18 9 L18 13 L29 19 L29 22 L18 19 L18 26 L21 29 L21 30.5 L16 29.5 L11 30.5 L11 29 L14 26 L14 19 L3 22 L3 19 L14 13 L14 9 Z";

// Icons are cached by their visual identity, not per aircraft. Leaflet replaces a marker's DOM
// whenever the icon object changes, so handing every aircraft a fresh icon on every 5-second
// tick would rebuild a few hundred nodes a tick on a phone. Rounding the heading to 5° and
// bucketing altitude collapses ~300 aircraft onto a few dozen shared icons that then stay
// identical between ticks.
const cache = new Map<string, L.DivIcon>();

// Labelled icons are per aircraft (the callsign is in the key), so they get their own bounded
// cache instead of growing the shared one. Only built at close zoom with at most ~60 aircraft in
// view (Radar.tsx), and a given aircraft's icon only changes when its heading crosses a 5° bucket.
const labelledCache = new Map<string, L.DivIcon>();
const LABELLED_CACHE_MAX = 400;

const escapeHtml = (text: string) =>
  text.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

export function aircraftIcon(opts: {
  track: number | undefined;
  alt: number | undefined;
  ground: boolean | undefined;
  selected: boolean;
  stale: boolean;
  /**
   * Callsign drawn inside the marker element itself. It used to be a separate permanent tooltip,
   * which Leaflet repositions independently on every frame of a fly-to; with the glide transition
   * those updates lagged and the label detached from its aircraft entirely. In the same element,
   * label and aircraft cannot separate.
   */
  label?: string;
}): L.DivIcon {
  const rotation = Math.round((opts.track ?? 0) / 5) * 5;
  const color = altitudeColor(opts.alt, opts.ground);
  const key = `${rotation}|${color}|${opts.selected ? 1 : 0}|${opts.stale ? 1 : 0}${opts.label ? `|${opts.label}` : ""}`;
  const store = opts.label ? labelledCache : cache;

  const existing = store.get(key);
  if (existing) return existing;

  // An aircraft with no track has nothing to point along, so it gets a dot rather than a
  // silhouette aimed arbitrarily north — a wrong heading looks like information.
  const body =
    opts.track === undefined
      ? `<circle cx="16" cy="16" r="5" fill="${color}" stroke="#0b1220" stroke-width="1.5"/>`
      : `<path d="${SILHOUETTE}" fill="${color}" stroke="#0b1220" stroke-width="1.5"
           stroke-linejoin="round" transform="rotate(${rotation} 16 16)"/>`;

  const ring = opts.selected
    ? `<circle cx="16" cy="16" r="14" fill="none" stroke="#ffffff" stroke-width="2" opacity="0.9"/>`
    : "";

  // Outside the SVG, so it doesn't rotate with the silhouette.
  const tag = opts.label ? `<span class="aircraft-tag">${escapeHtml(opts.label)}</span>` : "";

  const icon = L.divIcon({
    className: "aircraft-icon",
    html: `<svg width="32" height="32" viewBox="0 0 32 32" style="opacity:${opts.stale ? 0.35 : 1}">${ring}${body}</svg>${tag}`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
  if (store === labelledCache && labelledCache.size >= LABELLED_CACHE_MAX) {
    // Maps iterate in insertion order, so the first key is the oldest.
    labelledCache.delete(labelledCache.keys().next().value!);
  }
  store.set(key, icon);
  return icon;
}
